import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';

const EmployeesPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    employees: false,
    work_information: false,
    resume: false,
    certifications: false,
    personal_details: false,
    citizenship_identity: false
  });
  
  // Employees pages mapping from permissions.js
  const employeesPages = [
    'employees', 
    'work_information', 
    'resume', 
    'certifications', 
    'personal_details', 
    'citizenship_identity'
  ];
  
  const pageNameMapping = {
    'employees': 'Employees Page',
    'work_information': 'Work Information Tab',
    'resume': 'Resume Tab',
    'certifications': 'Certifications Tab',
    'personal_details': 'Personal Details Tab',
    'citizenship_identity': 'Citizenship & Identity Tab'
  };
  
  const pageIconMapping = {
    'employees': <PeopleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'work_information': <WorkIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'resume': <DescriptionIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'certifications': <SchoolIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'personal_details': <PersonIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'citizenship_identity': <BadgeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderEmployeesPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'employees') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('employees.employees'));
    } else if (pageKey === 'work_information') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('employees.work_information'));
    } else if (pageKey === 'resume') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('employees.resume'));
    } else if (pageKey === 'certifications') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('employees.certifications'));
    } else if (pageKey === 'personal_details') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('employees.personal_details'));
    } else if (pageKey === 'citizenship_identity') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('employees.citizenship_identity'));
    }
    
    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete'));

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey !== 'citizenship_identity' && pageKey !== 'work_information' && deletePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission - not available for citizenship_identity and work_information */}
            {deletePerm && pageKey !== 'citizenship_identity' && pageKey !== 'work_information' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {employeesPages.filter(pageKey => pageKey !== 'citizenship_identity').map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        
        if (pageKey === 'personal_details') {
          return (
            <Box key={pageKey}>
              {renderEmployeesPagePermissions(pageKey, pageName)}
              
              {/* Citizenship & Identity as sub-section under Personal Details */}
              <Box sx={{ ml: 4, mt: 2 }}>
                <Box sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  background: alpha(theme.palette.error.main, 0.08),
                  border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500, 
                    color: 'error.main',
                    mb: 1,
                    fontSize: '0.9rem'
                  }}>
                    SENSITIVE DATA WARNING
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    fontSize: '0.8rem'
                  }}>
                    Citizenship & Identity contains highly sensitive personal information including SSN, passport numbers, visa details, and government IDs. Only grant access to authorized personnel who require this data for legal/compliance purposes.
                  </Typography>
                </Box>
                {renderEmployeesPagePermissions('citizenship_identity', pageNameMapping['citizenship_identity'])}
              </Box>
            </Box>
          );
        }
        
        return renderEmployeesPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default EmployeesPermissions;
